// src/sections/notifications/NotificationProvider.jsx
// -----------------------------------------------------------------
// Global context that manages toast notifications + browser push.
//
// Usage:
//   <NotificationProvider>
//     <App />
//   </NotificationProvider>
//
//   const { showNotification, enablePush, pushEnabled } = useNotifications();
//   showNotification(notificationFromWebSocket);
//
// The provider renders a fixed-position toast container that shows
// up to 4 stacked toasts in the bottom-right corner.
//
// When the browser tab is in the background and push is enabled,
// it also fires a native Notification API push.
// -----------------------------------------------------------------

import React, { useState, useEffect, useCallback, createContext, useContext } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import {
  NotificationToastContainer,
  requestPushPermission,
  sendBrowserPush,
} from './NotificationToast';
import { normalizeActionLink } from '../../layouts/dashboard/header/NotificationsPopover';

// -----------------------------------------------------------------
// Context
// -----------------------------------------------------------------

const NotificationContext = createContext(null);

const MAX_TOASTS = 4;

// -----------------------------------------------------------------
// Provider
// -----------------------------------------------------------------

export default function NotificationProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const [pushEnabled, setPushEnabled] = useState(false);
  const navigate = useNavigate();

  // Check push permission on mount
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'granted') {
      setPushEnabled(true);
    }
  }, []);

  const enablePush = useCallback(async () => {
    const result = await requestPushPermission();
    const granted = result === 'granted';
    setPushEnabled(granted);
    return result;
  }, []);

  const showNotification = useCallback(
    (notification) => {
      // 1. Always show in-app toast
      setToasts((prev) => {
        const next = [notification, ...prev];
        return next.slice(0, MAX_TOASTS);
      });

      // 2. Send browser push if enabled AND tab is not focused
      if (pushEnabled && document.hidden) {
        sendBrowserPush(notification);
      }
    },
    [pushEnabled]
  );

  const dismissToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const handleAction = useCallback(
    (id, actionLink) => {
      // Find the toast so we can forward its data as navigation state
      const toast = toasts.find((t) => t.id === id);
      const navState = {
        shouldRedirectToOverview: true,
        ...(toast?.data?.goToTaskTab && { goToTaskTab: true, taskId: toast.data.taskId }),
      };

      dismissToast(id);
      if (actionLink) {
        // If the link already starts with '/' it's a full path (e.g. WhatsApp thread)
        if (actionLink.startsWith('/')) {
          navigate(actionLink, { state: navState });
        } else {
          const normalized = normalizeActionLink(actionLink);
          navigate(`/dashboard/${normalized}`, { state: navState });
        }
      }
    },
    [dismissToast, navigate, toasts]
  );

  const value = React.useMemo(
    () => ({ showNotification, enablePush, pushEnabled }),
    [showNotification, enablePush, pushEnabled]
  );

  return (
    <NotificationContext.Provider value={value}>
      {children}
      <NotificationToastContainer
        toasts={toasts}
        onDismiss={dismissToast}
        onAction={handleAction}
      />
    </NotificationContext.Provider>
  );
}

NotificationProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

// -----------------------------------------------------------------
// Hook
// -----------------------------------------------------------------

export function useNotifications() {
  const ctx = useContext(NotificationContext);
  if (!ctx) {
    throw new Error('useNotifications must be used within <NotificationProvider>');
  }
  return ctx;
}
